import { Assistance } from '../../../models/assistance'
import { AvailabilityCalendarLeaveEvent } from '../../worker-availability/worker-availability-calendar/AvailabilityCalendarLeaveEvent'
import { Client } from '../../../models/client'
import { DateUtils } from '../../../services/date-utils.service'
import { RruleHelper } from '../../recurrence/rruleHelper'
import { EventApi, EventContentArg, EventInput } from '@fullcalendar/core'
import { WorkerAvailability } from '../../../models/workerAvailability'
import { RecurrentFeathersModel } from '../../../models/RecurrentFeathersModel'
import invert from 'invert-color'
import { ColorUtils } from '../../../shared/utils/colorUtils'
import { hexToRgba } from '../../../shared/helpers/color.utils'
import { AvailabilityCalendarExchangeEnrollment } from '../../worker-availability/worker-availability-calendar/AvailabilityCalendarExchangeEnrollment'
import { addMinutes, differenceInMinutes, format } from 'date-fns'
import { CalendarAssistance } from '../../../models/CalendarAssistance'
import { CalendarWorker } from '../../../models/calendar-worker'
import { CalendarWorkerAvailability } from '../../../models/calendarWorkerAvailability'
import { CalendarWorkerAvailabilityAssistance } from '../../../models/CalendarWorkerAvailabilityAssistance'

interface FcEventConvertorOpts<T extends Assistance | CalendarAssistance> {
  titleFn?: (a: T) => string
  assistantId?: number
  shrinkEvents?: boolean
}

export enum ResourceSource {
  WORKERS = 'workers',
  CLIENTS = 'clients',
}

export enum EventSource {
  ASSISTANCES = 'assistances',
  AVAILABILITIES = 'availabilities',
  EXCHANGE_ENROLLMENTS = 'exchange_enrollments',
  LEAVE_EVENTS = 'leave_events',
}

export class FullCalendarHelper {
  static mapAssistancesToFCEventsForRoleAssistant(
    assistances: Assistance[],
    opts: FcEventConvertorOpts<Assistance> = {},
  ) {
    if (!opts.assistantId) return []

    if (!opts.titleFn)
      opts.titleFn = (_) => {
        return ''
      }

    return assistances
      .filter((a) => a.assistantAssignments.getAssignmentByAssistantId(opts.assistantId))
      .map((a) => {
        const assignment = a.assistantAssignments.getAssignmentByAssistantId(opts.assistantId)!
        const status = assignment.status
        const transit = assignment.transitDurationMinutes ?? 0
        const assistanceDurationMinutes = differenceInMinutes(a.getDateTo(), a.getDateFrom())

        const eventEnd = !opts.shrinkEvents
          ? addMinutes(a.getDateTo(), transit)
          : new Date(a.getDateFrom().getTime() + 1000)

        const event: any = {
          id: a.id.toString(),
          resourceId: FullCalendarHelper.clientIdToResourceId(a.clientId, ResourceSource.CLIENTS),
          title: opts.titleFn(a) || '',
          start: a.getDateFrom(),
          end: eventEnd,
          backgroundColor: status.color,
          origin: a,
          extendedProps: {
            eventSource: EventSource.ASSISTANCES,
            transitDurationMinutes: transit,
            assistanceDurationMinutes,
          },
          textColor: invert(ColorUtils.colorNameToHexUsingCanvas(status.color), true),
        }

        if (a.rrule) {
          event.groupId = a.id
          event.rrule = this.generateFCEventRrule(a)
          event.duration = DateUtils.durationMs(new Date(a.plannedFrom), addMinutes(new Date(a.plannedTo), transit))
        }

        return event
      })
  }

  static mapAssistancesToFCEventsForAvailabilitiesCalendar(
    assistances: CalendarWorkerAvailabilityAssistance[],
    opts: FcEventConvertorOpts<Assistance> = {},
    assistantIdToWorkerIdMap: Map<number, number>,
  ) {
    if (!opts.titleFn) opts.titleFn = (_) => ''

    const events = []

    for (const a of assistances) {
      for (const assignment of a.assistantAssignments) {
        const statusHex = ColorUtils.colorNameToHexUsingCanvas(assignment.status.color)
        const workerId = assistantIdToWorkerIdMap.get(assignment.assistantId)
        if (!workerId) {
          continue
        }
        const transit = assignment.transitDurationMinutes ?? 0
        const assistanceDurationMinutes = differenceInMinutes(a.getEnd(), a.getStart())
        const eventEnd = addMinutes(a.getEnd(), transit)

        const event: any = {
          id: assignment.id,
          resourceId: FullCalendarHelper.workerIdToResourceId(workerId, ResourceSource.WORKERS),
          title: '',
          start: a.getStart(),
          end: eventEnd,
          // statusHex is `#rrggbb`; hexToRgba expects the digits without the leading '#'.
          backgroundColor: hexToRgba(statusHex.slice(1), 0.6),
          // Darkened so light status colours (e.g. yellow) still produce a visible border.
          borderColor: ColorUtils.darken(statusHex, 0.35),
          className: ['event-assistance', 'status-' + assignment.status.name],
          origin: assignment,
          textColor: invert(statusHex, true),
          extendedProps: {
            eventSource: EventSource.ASSISTANCES,
            // Original (non-darkened) status colour, used as the opaque fill on hover.
            assistanceColor: statusHex,
            clientName: a.clientFullName,
            isGroupSession: a.isGroupSession,
            addressStart: a.addressStart,
            addressEnd: a.addressEnd,
            transitDurationMinutes: transit,
            assistanceDurationMinutes,
          },
        }
        events.push(event)
      }
    }

    return events
  }

  static mapCalendarAssistancesToFCEvents(
    assistances: CalendarAssistance[],
    opts: FcEventConvertorOpts<CalendarAssistance> = {},
  ) {
    if (!opts.titleFn)
      opts.titleFn = (_) => {
        return ''
      }

    return assistances.map((a) => {
      const transit = a.longestTransitDurationMinutes ?? 0
      const assistanceDurationMinutes = differenceInMinutes(a.getDateTo(), a.getDateFrom())
      const eventEnd = addMinutes(a.getDateTo(), transit)

      const event: any = {
        id: a.id.toString(),
        resourceId: FullCalendarHelper.clientIdToResourceId(a.clientId, ResourceSource.CLIENTS),
        title: opts.titleFn(a) || '',
        start: a.getDateFrom(),
        end: eventEnd,
        backgroundColor: a.status.color,
        className: [
          'status-' + a.status.name,
          `cy-status-${a.status.name}`,
          `cy-date-from-${format(a.getDateFrom(), 'y-M-d-H-mm')}`,
          `cy-time-from-${format(a.getDateFrom(), 'H-mm')}`,
        ],
        origin: a,
        textColor: invert(ColorUtils.colorNameToHexUsingCanvas(a.status.color), true),
        extendedProps: {
          eventSource: EventSource.ASSISTANCES,
          transitDurationMinutes: transit,
          assistanceDurationMinutes,
        },
      }

      if (a.rrule) {
        event.groupId = a.id
        event.rrule = this.generateFCEventRrule(a)
        event.duration = DateUtils.durationMs(new Date(a.plannedFrom), addMinutes(new Date(a.plannedTo), transit))
      }

      if (a.isLateOrder) {
        event.className.push('lateOrderedEvent')
      }

      return event
    })
  }

  static mapClientsToFCResources(clients: Client[]) {
    return clients.map((c, index) => {
      return {
        index,
        id: FullCalendarHelper.clientIdToResourceId(c.id, ResourceSource.CLIENTS),
        title: c.user.fullName(),
        lastName: c.user.lastName, // for sorting resources
        firstName: c.user.firstName, // for sorting resources
        origin: c,
        extendedProps: {
          resourceSource: ResourceSource.CLIENTS,
        },
      }
    })
  }

  static isEventRecurrent(event: EventApi) {
    return !!event.groupId
  }

  static isClickedEventFirstInRecurrenceSeries(event: EventApi, origin: Assistance | WorkerAvailability): boolean {
    return (
      event.start.getTime() === origin.getDateFrom().getTime() && event.end.getTime() === origin.getDateTo().getTime()
    )
  }

  static isClickedEventFirstInRecurrenceSeriesNew(event: EventApi, origin: Assistance | WorkerAvailability): boolean {
    const eventStart = event.start.getTime()
    const eventEnd = event.end.getTime()

    // For WorkerAvailabilityCalendar model
    if ('plannedFrom' in origin && 'plannedTo' in origin) {
      const originStart = new Date(origin.plannedFrom).getTime()
      const originEnd = new Date(origin.plannedTo).getTime()
      return eventStart === originStart && eventEnd === originEnd
    }

    // For older models
    return this.isClickedEventFirstInRecurrenceSeries(event, origin)
  }

  static generateRruleStringFromRecurrentModel(model: RecurrentFeathersModel, isForCalendar = false): string | null {
    if (!model.rrule) return null

    let rrule = RruleHelper.addDTSTARTToRrule(model.getDateFrom(), RruleHelper.trim(model.rrule))

    if (isForCalendar) {
      if (model.recurrenceException) {
        rrule = RruleHelper.addEXDATEStoRrule(rrule, model.recurrenceException)
      }

      // TODO Until this bug https://github.com/fullcalendar/fullcalendar/issues/5726 is resolved, we need to use local dates everywhere on the FC rrule
      rrule = this.convertAllRruleUTCDatesToLocalTZ(rrule)
    } else {
      if (model.recurrenceException) {
        rrule = RruleHelper.addEXDATEStoRrule(rrule, model.recurrenceException, model.getDateFrom())
      }
      rrule = this.convertAllRruleUTCDatesToLocalTZOfDtStart(rrule)

      // Adjust UNTIL if DTSTART is in different TZ than UNTIL
      rrule = RruleHelper.adjustUNTILtimezoneToDtStart(rrule, model.getDateFrom())
    }

    return rrule
  }

  static generateFCEventRrule(model: RecurrentFeathersModel): string {
    return this.generateRruleStringFromRecurrentModel(model, true)
  }

  static buildTransitEventContent(
    statusColor: string,
    assistanceMinutes: number,
    transitMinutes: number,
    direction: 'row' | 'column' = 'row',
  ): HTMLElement | null {
    if (!transitMinutes) return null

    const total = assistanceMinutes + transitMinutes
    const assistancePct = (assistanceMinutes / total) * 100

    const wrapper = document.createElement('div')
    wrapper.style.cssText = `display:flex;flex-direction:${direction};height:100%;width:100%;overflow:hidden;border-radius:inherit;`

    const mainPart = document.createElement('div')
    mainPart.style.cssText = `flex:0 0 ${assistancePct}%;background-color:${statusColor};`

    const transitPart = document.createElement('div')
    transitPart.style.flex = '1 1 0%'
    transitPart.style.opacity = '0.5'
    transitPart.style.backgroundColor = statusColor
    transitPart.style.backgroundImage =
      'repeating-linear-gradient(' +
      '-45deg,' +
      'transparent 0,' +
      'transparent 2px,' +
      'color-mix(in srgb, var(--ion-color-dark-tint) 50%, transparent) 2px,' +
      'color-mix(in srgb, var(--ion-color-dark-tint) 50%, transparent) 4px)'

    wrapper.appendChild(mainPart)
    wrapper.appendChild(transitPart)
    return wrapper
  }

  static buildTransitDom(arg: EventContentArg, direction: 'row' | 'column' = 'row'): HTMLElement | undefined {
    const transit: number = arg.event.extendedProps['transitDurationMinutes'] ?? 0
    if (!transit) return undefined
    const assistanceMinutes: number = arg.event.extendedProps['assistanceDurationMinutes'] ?? 0
    return (
      FullCalendarHelper.buildTransitEventContent(arg.event.backgroundColor, assistanceMinutes, transit, direction) ??
      undefined
    )
  }

  private static convertAllRruleUTCDatesToLocalTZ(rrule: string) {
    let rruleInLocalTz = rrule

    const r = /(\d{8}T)(\d{2})(\d{4})Z/gs

    let match: any[]
    while ((match = r.exec(rrule)) != null) {
      const dateTZoffset = RruleHelper.icalToDate(match[0]).getTimezoneOffset() / 60
      const localTzHour = DateUtils.zeropad(Number(match[2]) - dateTZoffset, 2)
      rruleInLocalTz = rruleInLocalTz.replace(match[0], match[1] + localTzHour + match[3]) // TODO: test for 23 and zero in hours
    }

    return rruleInLocalTz
  }

  private static convertAllRruleUTCDatesToLocalTZOfDtStart(rrule: string) {
    let rruleInLocalTz = rrule

    const r = /(\d{8}T)(\d{2})(\d{4})Z/gs

    let match: any[]
    let dtStartTZOffset: number

    while ((match = r.exec(rrule)) != null) {
      const dateTZoffset = RruleHelper.icalToDate(match[0]).getTimezoneOffset() / 60
      if (!dtStartTZOffset) dtStartTZOffset = dateTZoffset
      const localTzHour = DateUtils.zeropad(Number(match[2]) - dtStartTZOffset, 2)
      rruleInLocalTz = rruleInLocalTz.replace(match[0], match[1] + localTzHour + match[3]) // TODO: test for 23 and zero in hours
    }

    return rruleInLocalTz
  }

  static mapAvailabilitiesToFCEvents(workerAvailabilities: WorkerAvailability[]): EventInput[] {
    return workerAvailabilities.map((a) => {
      const event: EventInput = {
        id: a.id.toString(),
        resourceId: FullCalendarHelper.workerIdToResourceId(a.worker.id, ResourceSource.WORKERS),
        title: '',
        start: a.getDateFrom(),
        end: a.getDateTo(),
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue(
          '--app-availability-background-default',
        ),
        origin: a,
        extendedProps: {
          eventSource: EventSource.AVAILABILITIES,
        },
      }

      if (a.rrule) {
        event.groupId = a.id.toString()
        event.rrule = this.generateFCEventRrule(a)
        event.duration = DateUtils.durationMs(a.getDateFrom(), a.getDateTo())
      }

      return event
    })
  }

  static mapAvailabilitiesToFCEventsNew(workerAvailabilities: CalendarWorkerAvailability[]): EventInput[] {
    return workerAvailabilities.map((a) => {
      const event: EventInput = {
        id: a.id.toString(),
        resourceId: FullCalendarHelper.workerIdToResourceId(a.workerId, ResourceSource.WORKERS),
        title: '',
        start: a.getDateFrom(),
        end: a.getDateTo(),
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue(
          '--app-availability-background-default',
        ),
        origin: a,
        extendedProps: {
          eventSource: EventSource.AVAILABILITIES,
        },
      }

      if (a.rrule) {
        event.groupId = a.id.toString()
        event.rrule = this.generateFCEventRrule(a)
        event.duration = DateUtils.durationMs(a.getDateFrom(), a.getDateTo())
      }

      return event
    })
  }

  static mapWorkersToFCResources(workers: CalendarWorker[], resourceSource = ResourceSource.WORKERS) {
    return workers.map((w, index) => {
      return {
        index,
        id: FullCalendarHelper.workerIdToResourceId(w.id, resourceSource),
        title: w.firstName + ' ' + w.lastName,
        lastName: w.lastName, // for sorting resources
        firstName: w.firstName, // for sorting resources
        origin: w,
        extendedProps: {
          resourceSource,
        },
      }
    })
  }

  static mapExchangeEnrollmentsToFCEvents(
    exchangeEnrollments: AvailabilityCalendarExchangeEnrollment[],
    assistantIdToWorkerIdMap: Map<number, number>,
  ) {
    return exchangeEnrollments
      .map((e) => {
        const workerId = assistantIdToWorkerIdMap.get(e.assistantId)
        if (!workerId) {
          return
        }
        const event: EventInput = {
          id: e.id.toString(),
          resourceId: FullCalendarHelper.workerIdToResourceId(workerId, ResourceSource.WORKERS),
          title: '',
          start: e.start,
          end: e.end,
          className: [e.enrolledAt ? 'is-enrolled' : 'is-unenrolled'],
          origin: e,
          extendedProps: {
            eventSource: EventSource.EXCHANGE_ENROLLMENTS,
            clientName: e.assistance.client?.user?.fullname,
            addressStart: e.assistance.addressStart,
            addressEnd: e.assistance.addressEnd,
            isCanceled: !e.enrolledAt,
          },
        }
        return event
      })
      .filter((e) => !!e)
  }

  static mapLeaveEventsToFCEvents(
    leaveEvents: AvailabilityCalendarLeaveEvent[],
    setResource: boolean = true,
  ): EventInput[] {
    return leaveEvents.map((e) => {
      const event: EventInput = {
        id: e.urid,
        title: e.typeName ?? '',
        start: e.start,
        end: e.end,
        className: 'event-leave',
        editable: false,
        origin: e,
        extendedProps: {
          eventSource: EventSource.LEAVE_EVENTS,
        },
      }
      if (setResource) {
        event.resourceId = FullCalendarHelper.workerIdToResourceId(e.workerId, ResourceSource.WORKERS)
      }
      return event
    })
  }

  /**
   * Converts resource id in form 'assistantId-resourcesource' to 'assistantId'
   */
  static resourceIdToAssistantId(resourceId: string): number {
    return Number(resourceId.split('-')[0])
  }

  /**
   * Converts resource id in form 'workerId-resourcesource' to 'workerId'
   */
  static resourceIdToWorkerId(resourceId: string): number {
    return Number(resourceId.split('-')[0])
  }

  /**
   * Converts resource id in form 'clientId-resourcesource' to 'clientId'
   */
  static resourceIdToClientId(resourceId: string): number {
    return this.resourceIdToAssistantId(resourceId)
  }

  /**
   * Converts assistant id to resourceId in form 'assistantId-resourcesource'
   */
  static assistantIdToResourceId(assistantId: number, resourceSource: ResourceSource) {
    return `${assistantId.toString()}-${resourceSource}`
  }

  /**
   * Converts worker ID to resourceId in form 'workerId-resourcesource'
   */
  static workerIdToResourceId(workerId: number, resourceSource: ResourceSource) {
    return `${workerId.toString()}-${resourceSource}`
  }

  /**
   * Converts client ID to resourceId in form 'clientId-resourcesource'
   */
  static clientIdToResourceId(clientId: number, resourceSource: ResourceSource) {
    return this.assistantIdToResourceId(clientId, resourceSource)
  }
}
